import { Badge, Table } from 'reactstrap';
import {
    calcularRiesgoResidualInstitucional,
    diagnosticarRiesgoResidual,
} from '../wizard/utils';
import { TIPO_ACTIVIDAD_VULNERABLE } from '../Utils.global';

export function calcularPuntajeRiesgo(cuestionario) {
    const preguntas = (cuestionario || []).filter(
        (p) => String(p.tipo || '').trim() === 'frequency' && !Number.isNaN(parseFloat(p.peso_default))
    );
    if (!preguntas.length) return null;

    const sumaPesos = preguntas.reduce((s, p) => s + parseFloat(p.peso_default), 0);
    if (!sumaPesos) return null;

    const ponderado = preguntas.reduce((s, p) => {
        return s + ((Number(p.respuesta) - 1) / 4) * parseFloat(p.peso_default);
    }, 0);

    const normalizado = ponderado / sumaPesos;
    return 1 + normalizado * 2;
}

export function calcularPromedioEficacia(mitigantes) {
    const conRespuesta = (mitigantes || []).filter(
        (m) => m.respuesta !== null && m.respuesta !== undefined && m.respuesta !== ''
    );
    if (!conRespuesta.length) return null;
    const suma = conRespuesta.reduce((s, m) => s + Number(m.respuesta), 0);
    return suma / conRespuesta.length;
}

export function resumenEvaluacion(cuestionario, mitigantes) {
    const puntaje = calcularPuntajeRiesgo(cuestionario);
    const promedioEficacia = calcularPromedioEficacia(mitigantes);
    const riesgoResidual = calcularRiesgoResidualInstitucional(puntaje, promedioEficacia);
    const diagnosticoResidual = diagnosticarRiesgoResidual(riesgoResidual);
    return { puntaje, promedioEficacia, riesgoResidual, diagnosticoResidual };
}

export function getDisplayNameActividad(actividadVulnerable) {
    const valores = TIPO_ACTIVIDAD_VULNERABLE
        .filter((t) => t.value)
        .map((t) => t.value);
    if (valores.includes(actividadVulnerable)) {
        return TIPO_ACTIVIDAD_VULNERABLE.find((t) => t.value === actividadVulnerable)?.name;
    }
    return actividadVulnerable;
}

function agruparPorDimension(cuestionario) {
    return (cuestionario || []).reduce((acc, p) => {
        const key = p.dimension || 'sin_dimension';
        if (!acc[key]) acc[key] = [];
        acc[key].push(p);
        return acc;
    }, {});
}

function etiquetaDimension(dimension) {
    if (dimension === 'common') return 'Comunes';
    if (dimension.startsWith('module:')) return dimension.replace('module:', '');
    return dimension;
}

function etiquetaRespuesta(pregunta) {
    const { tipo, respuesta } = pregunta;
    if (respuesta === '' || respuesta === null || respuesta === undefined) return '—';
    const tipoNorm = String(tipo || '').trim();
    if (tipoNorm === 'frequency') {
        const etiquetas = { 1: 'Nunca', 2: 'Raramente', 3: 'Ocasionalmente', 4: 'Frecuentemente', 5: 'Muy frecuente' };
        return `${respuesta} — ${etiquetas[respuesta] || ''}`;
    }
    if (tipoNorm === 'money') {
        return `$${Number(respuesta).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
    }
    if (tipoNorm === 'boolean') {
        if (respuesta === true) return 'Sí';
        if (respuesta === false) return 'No';
    }
    if (tipoNorm === 'mitigante') return `${respuesta}%`;
    return String(respuesta);
}

function badgePeso(peso) {
    if (peso === 'BLOCKING') return <Badge color="danger">BLOQUEANTE</Badge>;
    if (peso === 'IMPORTANT') return <Badge color="warning" className="text-dark">IMPORTANTE</Badge>;
    const n = parseFloat(peso);
    if (!Number.isNaN(n)) return <Badge color="secondary">{(n * 100).toFixed(0)}%</Badge>;
    return <Badge color="light" className="text-dark">{peso}</Badge>;
}

export function DetalleRespuestasCuestionario({ cuestionario = [], mitigantes = [] }) {
    const grupos = agruparPorDimension(cuestionario);

    return (
        <div className="pt-2">
            {Object.entries(grupos).map(([dimension, preguntas]) => (
                <div key={dimension} className="mb-3">
                    <h6 className="border-bottom pb-1 mb-2 small fw-semibold">
                        {etiquetaDimension(dimension)}
                    </h6>
                    <Table size="sm" bordered responsive className="mb-0 bg-white">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: 70 }}>ID</th>
                                <th>Pregunta</th>
                                <th style={{ width: 80 }}>Peso</th>
                                <th style={{ width: 200 }}>Respuesta</th>
                            </tr>
                        </thead>
                        <tbody>
                            {preguntas.map((p) => (
                                <tr key={p.id}>
                                    <td className="text-muted">{p.id}</td>
                                    <td>{p.pregunta}</td>
                                    <td>{badgePeso(p?.peso_default)}</td>
                                    <td>{etiquetaRespuesta(p)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            ))}

            {mitigantes.length > 0 && (
                <div className="mb-0">
                    <h6 className="border-bottom pb-1 mb-2 small fw-semibold">Mitigantes y controles internos</h6>
                    <Table size="sm" bordered responsive className="mb-0 bg-white">
                        <thead className="table-light">
                            <tr>
                                <th>Control</th>
                                <th style={{ width: 120 }}>Eficacia</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mitigantes.map((m) => (
                                <tr key={m.id}>
                                    <td>{m.pregunta}</td>
                                    <td className="text-center">
                                        <Badge color={
                                            Number(m.respuesta) === 100 ? 'success'
                                                : Number(m.respuesta) === 50 ? 'warning'
                                                    : 'danger'
                                        }>
                                            {m.respuesta !== null && m.respuesta !== undefined ? `${m.respuesta}%` : '—'}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    );
}
